import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { MultiSelect } from 'primereact/multiselect';

import { RootState } from '../../../store';
import { alertService } from '../../../shared/services/alertService';
import { useGetInterDistrictQuery } from '../counselling.services';

/* eslint-disable @typescript-eslint/no-explicit-any */

/**
 * Migrated from Angular `InterDistrictComponent`
 * (src/app/views/pages/counselling/inter-district).
 *
 * Read-only report of inter-district teacher transfers for the logged-in user's
 * district, with a column chooser and global search. Reached from the CEO menu
 * (During Counselling → Inter District Transfers).
 */

interface ColumnDef {
  field: string;
  header: string;
}

const ALL_COLUMNS: ColumnDef[] = [
  { field: 'district_name', header: 'From District' },
  { field: 'udise_code', header: 'From UDISE' },
  { field: 'school_name', header: 'From School' },
  { field: 'Type', header: 'Type' },
  { field: 'seniority_state', header: 'Seniority' },
  { field: 'name', header: 'Teacher Name' },
  { field: 'teacher_id', header: 'Teacher Id' },
  { field: 'type_teacher', header: 'Designation' },
  { field: 'subjects', header: 'Subject' },
  { field: 'title', header: 'Counselling' },
  { field: 'district_name_t', header: 'To District' },
  { field: 'udise_code_t', header: 'To UDISE' },
  { field: 'school_name_t', header: 'To School' },
];

const GLOBAL_FILTER_FIELDS = ALL_COLUMNS.map((c) => c.field);

const InterDistrict = () => {
  const session = useSelector((state: RootState) => state.session);
  const districtId = session.userId ?? '';

  // Inter-district transfers for this district (Angular getInterDistrict).
  const { data } = useGetInterDistrictQuery({ districtId });

  const tableData: any[] = useMemo(() => {
    if (!data) return [];
    if (data.dataStatus) return data.result ?? [];
    if (data.message) alertService.warning(data.message);
    return [];
  }, [data]);

  // Column chooser — selected columns, kept in the original column order
  // (Angular selectedCol getter/setter).
  const [selectedCols, setSelectedCols] = useState<ColumnDef[]>(ALL_COLUMNS);
  const orderedCols = useMemo(
    () => ALL_COLUMNS.filter((col) => selectedCols.includes(col)),
    [selectedCols]
  );

  const [globalFilter, setGlobalFilter] = useState('');

  return (
    <div className="main-body-content">
      {/* Breadcrumb */}
      <div className="card">
        <div className="m-4">
          <div className="mt-4 Counselling-Secondary">
            <DataTable
              value={tableData}
              paginator
              rows={10}
              rowsPerPageOptions={[10, 25, 50]}
              rowHover
              responsiveLayout="scroll"
              emptyMessage="No Records Found"
              globalFilter={globalFilter}
              globalFilterFields={GLOBAL_FILTER_FIELDS}
              currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
              header={
                <div className="row">
                  <div className="col-md-3 col-6 mb-2">
                    <MultiSelect
                      value={selectedCols}
                      options={ALL_COLUMNS}
                      optionLabel="header"
                      placeholder="Choose Columns"
                      selectedItemsLabel="{0} columns selected"
                      className="w-100"
                      onChange={(e) => setSelectedCols(e.value)}
                    />
                  </div>
                  <div className="col-md-9 col-12 text-end">
                    <span className="p-input-icon-left p-ml-auto">
                      <InputText
                        type="text"
                        placeholder="Search keyword"
                        value={globalFilter}
                        onChange={(e) => setGlobalFilter(e.target.value)}
                      />
                    </span>
                  </div>
                </div>
              }
            >
              <Column header="#" body={(_r, opt) => opt.rowIndex + 1} />
              {orderedCols.map((col) => (
                <Column
                  key={col.field}
                  field={col.field}
                  header={col.header}
                  sortable
                />
              ))}
            </DataTable>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InterDistrict;

/* eslint-enable @typescript-eslint/no-explicit-any */
